import { IdeaStop } from '../../../../../../types/ideaStop.types';

export type IdeaStopFieldType = 'text' | 'dateTime' | 'date' | 'time' | 'address' | 'airport' | 'country';

export interface IdeaStopField {
  key: string;
  label: string;
  type: IdeaStopFieldType;
  getValue: (ideaStop: IdeaStop) => string | null;
  sectionDividerBefore?: boolean;
  sectionDividerAfter?: boolean;
  fieldGroupId?: string;
}

const getAddressName = (address: any): string | null => {
  return address?.name || null;
};

const flightFields: IdeaStopField[] = [
  {
    key: 'flight_from',
    label: 'From',
    type: 'airport',
    getValue: (ideaStop: IdeaStop) => ideaStop.flight_from || null,
  },
  {
    key: 'flight_to',
    label: 'To',
    type: 'airport',
    getValue: (ideaStop: IdeaStop) => ideaStop.flight_to || null,
  },
  {
    key: 'departure_date_time',
    label: 'Departure',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.departure_date_time || null,
  },
  {
    key: 'arrival_date_time',
    label: 'Arrival',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.arrival_date_time || null,
  },
  {
    key: 'airline',
    label: 'Airline',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.airline || null,
    sectionDividerBefore: true,
  },
  {
    key: 'flight_number',
    label: 'Flight #',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.flight_number || null,
  },
];

const hotelFields: IdeaStopField[] = [
  {
    key: 'name',
    label: 'Hotel Name',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.name || null,
  },
  {
    key: 'check_in_date_time',
    label: 'Check-in',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.check_in_date_time || null,
  },
  {
    key: 'check_out_date_time',
    label: 'Check-out',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.check_out_date_time || null,
  },
  {
    key: 'address',
    label: 'Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.address),
    sectionDividerAfter: true,
  },
];

const activityFields: IdeaStopField[] = [
  {
    key: 'name',
    label: 'Name',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.name || null,
  },
  {
    key: 'start_date_time',
    label: 'Start',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.start_date_time || null,
  },
  {
    key: 'end_date_time',
    label: 'End',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.end_date_time || null,
  },
  {
    key: 'place',
    label: 'Place',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.place || null,
  },
  {
    key: 'address',
    label: 'Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.address),
  },
];

const carRentalFields: IdeaStopField[] = [
  {
    key: 'start_date_time',
    label: 'Pickup',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.start_date_time || null,
  },
  {
    key: 'end_date_time',
    label: 'Drop-off',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.end_date_time || null,
  },
  {
    key: 'transport_name',
    label: 'Vehicle',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.transport_name || null,
  },
  {
    key: 'address',
    label: 'Office Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.address),
  },
];

const culturalEventFields: IdeaStopField[] = [
  {
    key: 'name',
    label: 'Event Name',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.name || null,
  },
  {
    key: 'start_date_time',
    label: 'Start',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.start_date_time || null,
  },
  {
    key: 'end_date_time',
    label: 'End',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.end_date_time || null,
  },
  {
    key: 'place',
    label: 'Venue',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.place || null,
  },
  {
    key: 'address',
    label: 'Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.address),
  },
];

const outingFields: IdeaStopField[] = [
  {
    key: 'name',
    label: 'Name',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.name || null,
  },
  {
    key: 'start_date_time',
    label: 'Start',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.start_date_time || null,
  },
  {
    key: 'end_date_time',
    label: 'End',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.end_date_time || null,
  },
  {
    key: 'place',
    label: 'Location',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.place || null,
  },
  {
    key: 'address',
    label: 'Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.address),
  },
];

const parkingFields: IdeaStopField[] = [
  {
    key: 'place',
    label: 'Parking Location',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.place || null,
  },
  {
    key: 'start_date_time',
    label: 'Start',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.start_date_time || null,
  },
  {
    key: 'end_date_time',
    label: 'End',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.end_date_time || null,
  },
  {
    key: 'address',
    label: 'Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.address),
  },
];

const directionsFields: IdeaStopField[] = [
  {
    key: 'place',
    label: 'Destination',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.place || null,
  },
  {
    key: 'start_date_time',
    label: 'Start',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.start_date_time || null,
  },
  {
    key: 'end_date_time',
    label: 'End',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.end_date_time || null,
  },
  {
    key: 'address',
    label: 'Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.address),
  },
];

const borderCrossingFields: IdeaStopField[] = [
  {
    key: 'country_from',
    label: 'From Country',
    type: 'country',
    getValue: (ideaStop: IdeaStop) => ideaStop.country_from || null,
  },
  {
    key: 'country_to',
    label: 'To Country',
    type: 'country',
    getValue: (ideaStop: IdeaStop) => ideaStop.country_to || null,
  },
  {
    key: 'border_checkpoint',
    label: 'Checkpoint',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.border_checkpoint || null,
  },
  {
    key: 'address',
    label: 'Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.address),
    sectionDividerAfter: true,
  },
];

const ferryFields: IdeaStopField[] = [
  {
    key: 'name',
    label: 'Ferry Name',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.name || null,
  },
  {
    key: 'departure_date_time',
    label: 'Departure',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.departure_date_time || null,
  },
  {
    key: 'arrival_date_time',
    label: 'Arrival',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.arrival_date_time || null,
  },
  {
    key: 'location_from_name',
    label: 'From',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.location_from_name || null,
  },
  {
    key: 'location_to_name',
    label: 'To',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.location_to_name || null,
  },
  {
    key: 'departure_address',
    label: 'Departure Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.departure_address),
  },
  {
    key: 'arrival_address',
    label: 'Arrival Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.arrival_address),
    sectionDividerAfter: true,
  },
];

const cruiseFields: IdeaStopField[] = [
  {
    key: 'name',
    label: 'Cruise Name',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.name || null,
  },
  {
    key: 'transport_name',
    label: 'Ship Name',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.transport_name || null,
  },
  {
    key: 'departure_date_time',
    label: 'Departure',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.departure_date_time || null,
  },
  {
    key: 'arrival_date_time',
    label: 'Arrival',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.arrival_date_time || null,
  },
  {
    key: 'location_from_name',
    label: 'Starting Port',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.location_from_name || null,
  },
  {
    key: 'location_to_name',
    label: 'Ending Port',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.location_to_name || null,
  },
  {
    key: 'starting_port_address',
    label: 'Starting Port Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.starting_port_address),
  },
  {
    key: 'ending_port_address',
    label: 'Ending Port Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.ending_port_address),
    sectionDividerAfter: true,
  },
];

const railFields: IdeaStopField[] = [
  {
    key: 'name',
    label: 'Train Name',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.name || null,
  },
  {
    key: 'departure_date_time',
    label: 'Departure',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.departure_date_time || null,
  },
  {
    key: 'arrival_date_time',
    label: 'Arrival',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.arrival_date_time || null,
  },
  {
    key: 'location_from_name',
    label: 'Departure Station',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.location_from_name || null,
  },
  {
    key: 'location_to_name',
    label: 'Arrival Station',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.location_to_name || null,
  },
  {
    key: 'departure_address',
    label: 'Departure Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.departure_address),
  },
  {
    key: 'arrival_address',
    label: 'Arrival Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.arrival_address),
    sectionDividerAfter: true,
  },
];

const transportationFields: IdeaStopField[] = [
  {
    key: 'vehicle_type',
    label: 'Vehicle Type',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.vehicle_type || null,
  },
  {
    key: 'departure_date_time',
    label: 'Departure',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.departure_date_time || null,
  },
  {
    key: 'arrival_date_time',
    label: 'Arrival',
    type: 'dateTime',
    getValue: (ideaStop: IdeaStop) => ideaStop.arrival_date_time || null,
  },
  {
    key: 'location_from_name',
    label: 'From',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.location_from_name || null,
  },
  {
    key: 'location_to_name',
    label: 'To',
    type: 'text',
    getValue: (ideaStop: IdeaStop) => ideaStop.location_to_name || null,
  },
  {
    key: 'departure_address',
    label: 'Departure Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.departure_address),
  },
  {
    key: 'arrival_address',
    label: 'Arrival Address',
    type: 'address',
    getValue: (ideaStop: IdeaStop) => getAddressName(ideaStop.arrival_address),
    sectionDividerAfter: true,
  },
];

export const getIdeaStopFields = (stopType: string): IdeaStopField[] => {
  switch (stopType) {
    case 'Flight':
      return flightFields;
    case 'Hotel':
      return hotelFields;
    case 'Activity':
    case 'Meeting':
    case 'Tour':
      return activityFields;
    case 'CarRental':
      return carRentalFields;
    case 'Concert':
    case 'Theatre':
      return culturalEventFields;
    case 'Shopping':
    case 'Restaurant':
      return outingFields;
    case 'Parking':
      return parkingFields;
    case 'Directions':
      return directionsFields;
    case 'BorderCrossing':
      return borderCrossingFields;
    case 'Ferry':
      return ferryFields;
    case 'Cruise':
      return cruiseFields;
    case 'Rail':
      return railFields;
    case 'Transportation':
      return transportationFields;
    default:
      return [];
  }
};
